import { useState, useEffect } from 'react';
import { Search, Calendar, Truck as TruckIcon, User, Package, UserCircle, Filter } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface AssignmentHistory {
  id: string;
  truck_id: string;
  driver_id: string;
  trailer_id?: string;
  dispatcher_id?: string;
  assigned_at: string;
  released_at?: string;
  is_current: boolean;
  trucks?: {
    unit_number: string;
    make: string;
    model: string;
    trailers?: string;
  };
  drivers?: {
    first_name: string;
    last_name: string;
    driver_type: string;
  };
  trailers?: {
    unit_number: string;
  };
  dispatcher?: {
    full_name: string;
    email: string;
  };
}

export function HistoryView() {
  const toast = useToast();
  const [history, setHistory] = useState<AssignmentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'truck' | 'driver' | 'trailer' | 'dispatcher'>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    loadHistory();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, startDate, endDate]);

  async function loadHistory() {
    try {
      setLoading(true);

      // Get all equipment assignments with related data
      const { data: assignments, error } = await supabase
        .from('equipment_assignments')
        .select(`
          *,
          trucks(unit_number, make, model, dispatcher_id, trailers),
          drivers(first_name, last_name, driver_type),
          trailers(unit_number)
        `)
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      // Get all unique dispatcher IDs
      const dispatcherIds = [...new Set(
        assignments
          ?.map(a => a.trucks?.dispatcher_id)
          .filter(Boolean) || []
      )];

      // Fetch dispatcher info if there are any
      let dispatchers: any[] = [];
      if (dispatcherIds.length > 0) {
        const { data: dispatcherData } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', dispatcherIds);
        
        dispatchers = dispatcherData || [];
      }

      // Map dispatcher info to assignments
      const historyWithDispatchers = assignments?.map(assignment => ({
        ...assignment,
        dispatcher: dispatchers.find(d => d.id === assignment.trucks?.dispatcher_id)
      })) || [];

      setHistory(historyWithDispatchers);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load assignment history');
    } finally {
      setLoading(false);
    }
  }

  // Filter history based on search and filters
  const filteredHistory = history.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    
    // Search filter
    const matchesSearch = !searchTerm || 
      item.trucks?.unit_number.toLowerCase().includes(searchLower) ||
      `${item.drivers?.first_name} ${item.drivers?.last_name}`.toLowerCase().includes(searchLower) ||
      item.trailers?.unit_number?.toLowerCase().includes(searchLower) ||
      item.dispatcher?.full_name?.toLowerCase().includes(searchLower);

    // Type filter
    const matchesType = filterType === 'all' || 
      (filterType === 'truck' && item.truck_id) ||
      (filterType === 'driver' && item.driver_id) ||
      (filterType === 'trailer' && item.trailer_id) ||
      (filterType === 'dispatcher' && item.dispatcher_id);

    // Date range filter
    const assignedDate = new Date(item.assigned_at);
    const matchesStartDate = !startDate || assignedDate >= new Date(startDate);
    const matchesEndDate = !endDate || assignedDate <= new Date(endDate);

    return matchesSearch && matchesType && matchesStartDate && matchesEndDate;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredHistory.slice(startIndex, endIndex);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDuration = (start: string, end?: string) => {
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const days = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Less than 1 day';
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Assignment History</h1>
        <p className="text-slate-600 mt-1">
          Complete history of truck, driver, trailer, and dispatcher assignments
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search truck, driver, trailer, or dispatcher..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filter Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Filter By
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Assignments</option>
              <option value="truck">Trucks Only</option>
              <option value="driver">Drivers Only</option>
              <option value="trailer">Trailers Only</option>
              <option value="dispatcher">Dispatchers Only</option>
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              From Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              To Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Active Filters Summary */}
        {(searchTerm || filterType !== 'all' || startDate || endDate) && (
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
            <Filter className="w-4 h-4" />
            <span className="font-medium">Active filters:</span>
            {searchTerm && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Search: {searchTerm}</span>}
            {filterType !== 'all' && <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">Type: {filterType}</span>}
            {startDate && <span className="px-2 py-1 bg-green-100 text-green-700 rounded">From: {startDate}</span>}
            {endDate && <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">To: {endDate}</span>}
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
                setStartDate('');
                setEndDate('');
              }}
              className="ml-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-slate-600">
        Showing {startIndex + 1}-{Math.min(endIndex, filteredHistory.length)} of {filteredHistory.length} filtered records ({history.length} total)
      </div>

      {/* History Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Truck</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Driver</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Trailer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Dispatcher</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Assigned</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Unassigned</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    Loading history...
                  </td>
                </tr>
              ) : currentPageData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    No assignment history found
                  </td>
                </tr>
              ) : (
                currentPageData.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <TruckIcon className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="font-medium text-slate-900">
                            {item.trucks?.unit_number || 'N/A'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {item.trucks?.make} {item.trucks?.model}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="font-medium text-slate-900">
                            {item.drivers ? `${item.drivers.first_name} ${item.drivers.last_name}` : 'N/A'}
                          </div>
                          {item.drivers?.driver_type && (
                            <div className="text-xs">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                item.drivers.driver_type === 'owner_operator' ? 'bg-purple-100 text-purple-700' :
                                item.drivers.driver_type === 'company_driver' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {item.drivers.driver_type === 'owner_operator' ? 'OWNER' :
                                 item.drivers.driver_type === 'company_driver' ? 'COMPANY' :
                                 item.drivers.driver_type.toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.trucks?.trailers || item.trailers?.unit_number ? (
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-900">
                            {item.trucks?.trailers || item.trailers?.unit_number}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.dispatcher ? (
                        <div className="flex items-center gap-2">
                          <UserCircle className="w-4 h-4 text-slate-400" />
                          <div>
                            <div className="font-medium text-slate-900">
                              {item.dispatcher.full_name}
                            </div>
                            <div className="text-xs text-slate-500">
                              {item.dispatcher.email}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-slate-900">
                          {formatDate(item.assigned_at)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.released_at ? (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-red-600" />
                          <span className="text-sm text-slate-900">
                            {formatDate(item.released_at)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">
                        {calculateDuration(item.assigned_at, item.released_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.is_current 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {item.is_current ? 'Current' : 'Past'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm border rounded ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
